import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import {
  Building2,
  Mail,
  Lock,
  User,
  UserPlus,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Link as LinkIcon,
  Copy,
  Eye,
  EyeOff,
} from 'lucide-react'
import { getInvitationByToken, acceptInvitation, getSignupToken, useSignupToken } from '../services/onboarding'

const OnboardingPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [step, setStep] = useState(1) // 1: Check token/invite, 2: Sign up, 3: Success
  const [mode, setMode] = useState(null) // 'invitation', 'signup_token', 'new_org'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [dataRestored, setDataRestored] = useState(false)
  const [invitationData, setInvitationData] = useState(null)
  const [signupTokenData, setSignupTokenData] = useState(null)
  const [formData, setFormData] = useState(() => {
    // Restore form data from localStorage
    try {
      const saved = localStorage.getItem('onboardingPage_formData')
      if (saved) {
        const parsed = JSON.parse(saved)
        // Don't restore passwords for security
        const restored = {
          name: parsed.name || '',
          email: parsed.email || '',
          password: '',
          confirmPassword: '',
        }
        // Show indicator if data was restored
        if (parsed.email || parsed.name) {
          setTimeout(() => setDataRestored(true), 100)
          setTimeout(() => setDataRestored(false), 3000)
        }
        return restored
      }
    } catch (e) {
      console.error('Error restoring form data:', e)
    }
    return {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    }
  })

  // Save form data to localStorage whenever it changes (except passwords)
  useEffect(() => {
    const dataToSave = {
      name: formData.name,
      email: formData.email,
      // Don't save passwords for security
    }
    localStorage.setItem('onboardingPage_formData', JSON.stringify(dataToSave))
  }, [formData.name, formData.email])

  // Prevent page reload on visibility change (mobile lock/unlock)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Don't reload on visibility change - just keep the app running
      if (document.visibilityState === 'visible') {
        // Page became visible again - data should be restored from localStorage
        console.log('Page visible - form data should be restored')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Clear saved data on successful signup
  const clearSavedData = () => {
    localStorage.removeItem('onboardingPage_formData')
  }

  useEffect(() => {
    const invitationToken = searchParams.get('invite')
    const signupToken = searchParams.get('token')
    const action = searchParams.get('action')
    const reference = searchParams.get('reference')
    
    // Handle payment verification callback from Paystack
    if (action === 'verify' && reference) {
      verifyPayment(reference)
      return
    }
    
    if (invitationToken) {
      checkInvitation(invitationToken)
    } else if (signupToken) {
      checkSignupToken(signupToken)
    } else {
      setMode('new_org')
      setStep(1)
    }
  }, [searchParams])

  const checkInvitation = async (token) => {
    try {
      setLoading(true)
      const data = await getInvitationByToken(token)
      setInvitationData(data)
      setMode('invitation')
      setFormData((prev) => ({ ...prev, email: data.email }))
      setStep(2)
    } catch (error) {
      setError('Invalid or expired invitation link')
      setMode('new_org')
    } finally {
      setLoading(false)
    }
  }

  const checkSignupToken = async (token) => {
    try {
      setLoading(true)
      const data = await getSignupToken(token)
      setSignupTokenData(data)
      setMode('signup_token')
      setFormData((prev) => ({ ...prev, email: data.admin_email, name: data.admin_name || '' }))
      setStep(2)
    } catch (error) {
      setError('Invalid or expired signup token')
      setMode('new_org')
    } finally {
      setLoading(false)
    }
  }

  const verifyPayment = async (reference) => {
    try {
      setLoading(true)
      setError('')
      
      // Call Supabase Edge Function to verify payment
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const { data: { session } } = await supabase.auth.getSession()

      const response = await fetch(`${supabaseUrl}/functions/v1/paystack-payment/verify/${reference}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
      })

      if (!response.ok) {
        throw new Error('Payment verification failed')
      }

      const verifyData = await response.json()

      if (verifyData.status && verifyData.data?.status === 'success') {
        // Payment successful - redirect to dashboard
        setStep(3)
        clearSavedData()
        
        // Clean up URL
        navigate('/onboarding?payment=success', { replace: true })
        
        // Redirect to dashboard after a moment
        setTimeout(() => {
          navigate('/dashboard?onboarding=complete')
        }, 2000)
      } else {
        throw new Error('Payment verification failed or payment was not successful')
      }
    } catch (error) {
      console.error('Payment verification error:', error)
      setError(error.message || 'Payment verification failed. Please contact support if payment was deducted.')
      setMode('new_org')
      setStep(1)
    } finally {
      setLoading(false)
    }
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Validate passwords match
      if (formData.password !== formData.confirmPassword) {
        throw new Error('Passwords do not match')
      }

      // Sign up user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name,
          },
        },
      })

      if (authError) throw authError

      // Wait a moment for user to be created
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Clear saved form data on successful signup
      clearSavedData()

      // Handle based on mode
      if (mode === 'invitation') {
        const invitationToken = searchParams.get('invite')
        const result = await acceptInvitation(invitationToken)
        if (!result.success) {
          throw new Error(result.error || 'Failed to accept invitation')
        }
      } else if (mode === 'signup_token') {
        const signupToken = searchParams.get('token')
        const result = await useSignupToken(signupToken, formData.name, formData.email)
        if (!result.success) {
          throw new Error(result.error || 'Failed to use signup token')
        }
      }
      // If mode is 'new_org', the trigger will handle it automatically

      setStep(3)
    } catch (err) {
      setError(err.message || 'An error occurred during signup')
    } finally {
      setLoading(false)
    }
  }

  const getModeTitle = () => {
    switch (mode) {
      case 'invitation':
        return `Join ${invitationData?.organizations?.name || 'Organization'}`
      case 'signup_token':
        return `Create ${signupTokenData?.organization_name || 'Organization'}`
      default:
        return 'Create Your Organization'
    }
  }

  const getModeDescription = () => {
    switch (mode) {
      case 'invitation':
        return `You've been invited to join as ${invitationData?.role || 'member'}`
      case 'signup_token':
        return 'You have a signup token to create a new organization'
      default:
        return 'Start your 30-day free trial'
    }
  }

  if (loading && step === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Preparing your workspace...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card variant="glass" className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center shadow-lg"
            >
              <Building2 size={40} className="text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              {step === 3 ? 'Welcome!' : getModeTitle()}
            </h1>
            <p className="text-gray-600 mt-2">{step === 3 ? 'Your account is ready' : getModeDescription()}</p>
          </div>

          {/* Data Restored Indicator */}
          {dataRestored && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200"
            >
              ✓ Your form data has been restored
            </motion.div>
          )}

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step 1: Mode Selection (if no token/invite) */}
          {step === 1 && mode === 'new_org' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <p className="text-center text-gray-600 mb-6">
                Choose how you'd like to get started
              </p>
              <Button
                onClick={() => {
                  setStep(2)
                }}
                className="w-full"
              >
                Create New Organization
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>
              <p className="text-sm text-center text-gray-600">
                Have an invitation link?{' '}
                <button
                  onClick={() => {
                    const token = prompt('Enter your invitation token:')
                    if (token) {
                      navigate(`/onboarding?invite=${token}`)
                    }
                  }}
                  className="text-primary-600 font-semibold hover:text-primary-700"
                >
                  Use it here
                </button>
              </p>
            </motion.div>
          )}

          {/* Step 2: Sign Up Form */}
          {step === 2 && (
            <motion.form
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleSignup}
              className="space-y-4"
            >
              <div className="relative">
                <User
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <Input
                  type="text"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="pl-10"
                />
              </div>

              <div className="relative">
                <Mail
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={mode === 'invitation' || mode === 'signup_token'}
                  className="pl-10"
                />
              </div>

              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {mode === 'invitation' && invitationData && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>Role:</strong> {invitationData.role}
                  </p>
                  <p className="text-sm text-blue-800">
                    <strong>Organization:</strong> {invitationData.organizations?.name}
                  </p>
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </motion.form>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center space-y-6"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center"
              >
                <CheckCircle2 size={32} className="text-green-600" />
              </motion.div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Account Created!</h2>
                <p className="text-gray-600">
                  {mode === 'invitation'
                    ? "You've been added to the organization. Check your email to verify your account."
                    : mode === 'signup_token'
                    ? 'Your organization has been created. Check your email to verify your account.'
                    : 'Your organization has been created. Check your email to verify your account.'}
                </p>
              </div>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                Go to Dashboard
              </Button>
            </motion.div>
          )}

          {/* Footer */}
          {step !== 3 && (
            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                Already have an account?{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-primary-600 font-semibold hover:text-primary-700"
                >
                  Sign In
                </button>
              </p>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  )
}

export default OnboardingPage

